// src/admin/components/assets/AccessKeysPanel.jsx
import React from 'react';
import PropTypes from 'prop-types';
import CopyButton from '../common/CopyButton';
import { WorkflowTypeBadges } from '../../../shared/components/WorkflowTypeBadge';
import { formatDate } from '../../../shared/utils/formatDate';

/**
 * Gets display name for an access key
 */
function getAccessKeyName(accessKey) {
  // If it came from a short URL, prefer the attached workflow name (when
  // the QR was wired to launch a specific workflow); otherwise fall back
  // to the generic "Permanent Access" label.
  if (accessKey.short_url) {
    return accessKey.short_url.workflow?.name || 'Permanent Access';
  }
  if (accessKey.delivered_to) {
    return accessKey.delivered_to.name || accessKey.delivered_to.email;
  }
  if (accessKey.task) {
    return accessKey.task.assigned_to_email;
  }
  return 'Access Key';
}

/**
 * Gets the icon for an access key based on its type
 */
function getAccessKeyIcon(accessKey) {
  if (accessKey.short_url) {
    // QR code / permanent access
    return 'bi-qr-code';
  }
  if (accessKey.delivered_to || accessKey.task) {
    // Person / user
    return 'bi-person-circle';
  }
  return 'bi-key';
}

/**
 * Access key card item
 */
function AccessKeyItem({ accessKey }) {
  const name = getAccessKeyName(accessKey);
  const icon = getAccessKeyIcon(accessKey);
  const isExpired = accessKey.expired;
  const isPermanent = !accessKey.expires_at;
  const fullUrl = `${window.location.origin}/assets/access_key/${accessKey.key}`;
  const linkedWorkflow = accessKey.short_url?.workflow;

  return (
    <div className={`border rounded p-3 mb-2 ${isExpired ? 'bg-light' : 'bg-white'}`}>
      {/* Row 1: Icon, Name, Copy Button */}
      <div className="d-flex align-items-start justify-content-between mb-1">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <i className={`bi ${icon} fs-5 ${isExpired ? 'text-muted' : 'text-success'}`} />
          <span className={`fw-semibold ${isExpired ? 'text-muted' : ''}`}>
            {name}
          </span>
          {linkedWorkflow && <WorkflowTypeBadges workflow={linkedWorkflow} />}
        </div>
        {!isExpired && (
          <CopyButton
            value={fullUrl}
            title="Copy access URL"
          />
        )}
      </div>
      {/* Row 2: Expiry info and Status (aligned right) */}
      <div className="d-flex align-items-center justify-content-between">
        <span className={`small ${isExpired ? 'text-muted' : 'text-muted'}`}>
          {isExpired ? (
            <>Expired: {formatDate(accessKey.expires_at, '')}</>
          ) : isPermanent ? (
            'Permanent'
          ) : (
            <>Expires: {formatDate(accessKey.expires_at, '')}</>
          )}
        </span>
        <span className={`small ${isExpired ? 'text-muted' : 'text-success'}`}>
          {isExpired ? 'Expired' : 'Active'}
        </span>
      </div>
    </div>
  );
}

AccessKeyItem.propTypes = {
  accessKey: PropTypes.shape({
    id: PropTypes.number,
    key: PropTypes.string,
    expires_at: PropTypes.string,
    expired: PropTypes.bool,
    task: PropTypes.object,
    delivered_to: PropTypes.object,
    short_url: PropTypes.shape({
      id: PropTypes.number,
      code: PropTypes.string,
      workflow: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        user_workflow: PropTypes.bool,
      }),
    }),
  }).isRequired,
};

/**
 * Panel displaying all access keys for an asset
 */
export default function AccessKeysPanel({ accessKeys = [], onAddKey }) {
  // Split into active and expired
  const activeKeys = accessKeys.filter(ak => !ak.expired);
  const expiredKeys = accessKeys.filter(ak => ak.expired);

  return (
    <div className="card p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="fw-semibold mb-0">Access Keys</h5>
        {onAddKey && (
          <button
            type="button"
            className="btn btn-link p-0 text-primary"
            onClick={onAddKey}
            title="Add access key"
          >
            <i className="bi bi-plus-lg" />
          </button>
        )}
      </div>

      {/* Active keys */}
      {activeKeys.length > 0 && (
        <div className="mb-3">
          <div className="small text-muted fw-semibold mb-2">Valid Access</div>
          {activeKeys.map(ak => (
            // eslint-disable-next-line jsx-a11y/no-access-key -- domain prop: access-key record, not HTML accesskey
            <AccessKeyItem key={ak.id} accessKey={ak} />
          ))}
        </div>
      )}

      {/* Expired keys */}
      {expiredKeys.length > 0 && (
        <div>
          <div className="small text-muted fw-semibold mb-2">Recently Expired</div>
          {expiredKeys.map(ak => (
            // eslint-disable-next-line jsx-a11y/no-access-key -- domain prop: access-key record, not HTML accesskey
            <AccessKeyItem key={ak.id} accessKey={ak} />
          ))}
        </div>
      )}

      {/* Empty state */}
      {accessKeys.length === 0 && (
        <div className="text-center text-muted py-3">
          <i className="bi bi-key fs-1 d-block mb-2 opacity-50" />
          <p className="mb-0 small">No access keys for this asset</p>
        </div>
      )}
    </div>
  );
}

AccessKeysPanel.propTypes = {
  accessKeys: PropTypes.arrayOf(PropTypes.object),
  onAddKey: PropTypes.func,
};
